import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { motion } from "framer-motion";
import PageHeader from "./PageHeader";

const PromptTemplatesPage = () => {
  useEffect(() => {
    // Update document title and meta description for SEO
    document.title = "ChatGPT Prompt Templates | Power-Up Snippet Manager";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Save your best prompts as templates with ChatGPT Power-Up. Insert them in one click. Boost your productivity.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            ChatGPT Prompt Templates – Save Time, Type Less
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Transform your ChatGPT workflow with reusable prompt templates. Save your best prompts as snippets and insert them with one click – no more retyping or copy-pasting.
          </p>
        </motion.div>

        {/* What Are Prompt Templates */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What Are ChatGPT Prompt Templates?</h2>
          <p className="text-gray-700 mb-6">
            Prompt templates are pre-written, reusable prompts that you can save and insert instantly. Think of them as your personal library of proven prompts that work perfectly for your specific tasks and workflows.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-3">🎯 Perfect for Repeat Tasks</h3>
              <p className="text-blue-700">Save prompts for tasks you do regularly – code reviews, content creation, research questions, and more.</p>
            </div>
            <div className="bg-purple-50 p-6 rounded-lg">
              <h3 className="font-semibold text-purple-800 mb-3">⚡ Lightning Fast Access</h3>
              <p className="text-purple-700">Insert your best prompts instantly with one click or keyboard shortcuts. No more hunting through notes or chat history.</p>
            </div>
          </div>
        </motion.div>

        {/* How Extension Works */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">How ChatGPT Power-Up Adds Template Management</h2>
          <p className="text-gray-700 mb-6">
            Our extension seamlessly integrates a powerful snippet manager directly into ChatGPT's interface. You get a dedicated sidebar where you can save, organize, and access all your prompt templates.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">💾</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Save Templates</h3>
              <p className="text-gray-600">Save any prompt as a template with custom labels and categories for easy organization.</p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">📁</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Organize & Find</h3>
              <p className="text-gray-600">Group templates by project, task type, or any system that makes sense for your workflow.</p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">⚡</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Insert Instantly</h3>
              <p className="text-gray-600">Click any template to insert it instantly, or use keyboard shortcuts for even faster access.</p>
            </div>
          </div>
        </motion.div>

        {/* Example Templates */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Example Prompt Templates</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3">🔍 SEO Content</h3>
              <p className="text-gray-600 text-sm mb-3">"Write a comprehensive blog post about [topic] that targets the keyword [keyword]. Include H2 and H3 headings, and make it engaging for readers."</p>
              <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">Content Creation</span>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3">💻 Code Review</h3>
              <p className="text-gray-600 text-sm mb-3">"Review this [language] code for best practices, potential bugs, and optimization opportunities. Provide specific suggestions for improvement."</p>
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">Development</span>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3">📊 Data Analysis</h3>
              <p className="text-gray-600 text-sm mb-3">"Analyze this dataset and provide insights about [specific aspect]. Include visualizations and actionable recommendations."</p>
              <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">Analytics</span>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3">🎯 Marketing Copy</h3>
              <p className="text-gray-600 text-sm mb-3">"Create compelling ad copy for [product/service] targeting [audience]. Focus on benefits and include a strong call-to-action."</p>
              <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">Marketing</span>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3">📚 Research</h3>
              <p className="text-gray-600 text-sm mb-3">"Research [topic] and provide a comprehensive overview including key findings, trends, and implications for [industry/field]."</p>
              <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">Research</span>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3">✍️ Creative Writing</h3>
              <p className="text-gray-600 text-sm mb-3">"Write a [genre] story about [theme/concept]. Include vivid descriptions, engaging dialogue, and a compelling plot structure."</p>
              <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded">Creative</span>
            </div>
          </div>
        </motion.div>

        {/* Speed and Consistency */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-8 mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Speed and Consistency for Repeat Tasks</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">⚡ Save Time</h3>
              <p className="text-gray-700 mb-4">Instead of retyping the same prompts, insert them instantly. What used to take 30 seconds now takes 2 seconds.</p>
              <ul className="space-y-2 text-gray-600">
                <li>• One-click template insertion</li>
                <li>• Keyboard shortcuts for power users</li>
                <li>• No more copy-paste from notes</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">🎯 Maintain Quality</h3>
              <p className="text-gray-700 mb-4">Use your proven, tested prompts every time. No more forgetting important details or variations.</p>
              <ul className="space-y-2 text-gray-600">
                <li>• Consistent prompt structure</li>
                <li>• Proven templates that work</li>
                <li>• Easy to update and improve</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* How to Save a Prompt */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Save a Prompt Template</h2>
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="bg-blue-100 w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1">
                <span className="text-blue-600 font-bold">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Write Your Prompt</h3>
                <p className="text-gray-600">Create a prompt that works well for your specific task. Test it to make sure it produces the results you want.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="bg-blue-100 w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1">
                <span className="text-blue-600 font-bold">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Save as Template</h3>
                <p className="text-gray-600">Click the save button next to your prompt. Add a descriptive name and choose a category for easy organization.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="bg-blue-100 w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1">
                <span className="text-blue-600 font-bold">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Access Anywhere</h3>
                <p className="text-gray-600">Your template is now available in the sidebar. Click to insert it instantly in any new conversation.</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Related Content */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Explore More ChatGPT Power-Up Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/chatgpt-prompt-generator" className="block">
              <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-md transition-all">
                <h3 className="font-semibold text-gray-900 mb-2">ChatGPT Prompt Generator</h3>
                <p className="text-gray-600 text-sm">Learn how to save and reuse your best prompts with our powerful prompt generator.</p>
              </div>
            </Link>
            <Link to="/chatgpt-folders" className="block">
              <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-md transition-all">
                <h3 className="font-semibold text-gray-900 mb-2">ChatGPT Folders</h3>
                <p className="text-gray-600 text-sm">Organize your conversations into folders for better workflow management.</p>
              </div>
            </Link>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.4 }}
          className="text-center bg-gradient-to-r from-orange-600 to-red-600 rounded-xl p-8 text-white"
        >
          <h2 className="text-3xl font-bold mb-4">Start Using Prompt Templates Today</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of users who've transformed their ChatGPT workflow with templates
          </p>
          <a
            href="https://chromewebstore.google.com/detail/chatgpt-power-up/ooleaojggfoigcdkodigbcjnabidihgi"
            className="inline-block"
          >
            <Button className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
              Start Using Prompt Templates – Add to Chrome
            </Button>
          </a>
          <p className="text-sm opacity-75 mt-4">Free • No account required • Works instantly</p>
        </motion.div>
      </div>
    </div>
  );
};

export default PromptTemplatesPage; 